<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { messageStamp } from 'shared/helpers/timeHelper';
import WgptAccountAPI from 'dashboard/api/wgpt/account';
import BillingCard from './BillingCard.vue';
import ButtonV4 from 'next/button/Button.vue';

const balance = ref(0);
const packages = ref([]);
const history = ref([]);
const isLoading = ref(false);
const buyingId = ref(null);
const invoiceLoadingId = ref(null);

// Amounts come in the currency's minor units, and how many of them make a unit
// depends on the currency — 2000 usd is $20.00, 20000 jod is JOD 20.000.
const formatPrice = (amount, currency) => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  });
  const decimals = formatter.resolvedOptions().maximumFractionDigits;
  return formatter.format((amount || 0) / 10 ** decimals);
};

const formatDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy, h:mm a');

const listingLabel = listing =>
  listing.title ? `${listing.reference} — ${listing.title}` : listing.reference;

// The package with the lowest cost per listing, flagged as the best value.
// Only meaningful when there's more than one package to compare.
const bestValueId = computed(() => {
  if (packages.value.length < 2) return null;
  const ranked = packages.value
    .filter(pkg => pkg.listing_count > 0)
    .map(pkg => ({ id: pkg.id, perListing: pkg.amount / pkg.listing_count }))
    .sort((a, b) => a.perListing - b.perListing);
  return ranked[0]?.id ?? null;
});

const fetchCredits = async () => {
  isLoading.value = true;
  try {
    const { data } = await WgptAccountAPI.listingCredits();
    balance.value = data.balance;
    packages.value = data.packages;
    history.value = data.history;
  } catch (error) {
    useAlert(error?.message);
  } finally {
    isLoading.value = false;
  }
};

const buy = async pkg => {
  buyingId.value = pkg.id;
  try {
    const { data } = await WgptAccountAPI.listingCheckout(pkg.id);
    window.location = data.redirect_url;
  } catch (error) {
    useAlert(error?.message);
    buyingId.value = null;
  }
};

const openInvoice = async item => {
  invoiceLoadingId.value = item.id;
  try {
    const { data } = await WgptAccountAPI.listingCreditInvoice(item.id);
    window.open(data.redirect_url, '_blank', 'noopener');
  } catch (error) {
    useAlert(error?.message);
  } finally {
    invoiceLoadingId.value = null;
  }
};

onMounted(fetchCredits);
</script>

<template>
  <section class="grid gap-4">
    <BillingCard
      :title="$t('WGPT_BILLING.LISTING_CREDITS.TITLE')"
      :description="$t('WGPT_BILLING.LISTING_CREDITS.DESCRIPTION')"
    >
      <template #action>
        <ButtonV4
          sm
          flushed
          slate
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          @click="fetchCredits"
        >
          {{ $t('WGPT_BILLING.LISTING_CREDITS.REFRESH') }}
        </ButtonV4>
      </template>
      <div class="px-5">
        <p class="text-sm text-n-slate-11">
          {{ $t('WGPT_BILLING.LISTING_CREDITS.BALANCE') }}
        </p>
        <p class="text-2xl font-medium text-n-slate-12">{{ balance }}</p>
      </div>
    </BillingCard>

    <BillingCard
      :title="$t('WGPT_BILLING.LISTING_CREDITS.PACKAGES_TITLE')"
      :description="$t('WGPT_BILLING.LISTING_CREDITS.PACKAGES_DESCRIPTION')"
    >
      <div class="grid gap-3 px-5 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="pkg in packages"
          :key="pkg.id"
          class="relative flex flex-col gap-4 p-5 transition-colors border rounded-xl"
          :class="
            pkg.id === bestValueId
              ? 'border-n-blue-5'
              : 'border-n-weak hover:border-n-strong'
          "
        >
          <span
            v-if="pkg.id === bestValueId"
            class="absolute px-2 py-0.5 text-[11px] font-medium rounded-full top-3 text-n-blue-11 bg-n-blue-3 ltr:right-3 rtl:left-3"
          >
            {{ $t('WGPT_BILLING.LISTING_CREDITS.BEST_VALUE') }}
          </span>

          <div class="flex items-baseline gap-1.5">
            <span
              class="text-3xl font-semibold tracking-tight tabular-nums text-n-slate-12"
            >
              {{ pkg.listing_count }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ $t('WGPT_BILLING.LISTING_CREDITS.LISTINGS_LABEL') }}
            </span>
          </div>

          <div class="flex flex-col gap-0.5">
            <span class="text-lg font-medium tabular-nums text-n-slate-12">
              {{ formatPrice(pkg.amount, pkg.currency) }}
            </span>
            <span v-if="pkg.listing_count > 1" class="text-xs text-n-slate-10">
              {{
                $t('WGPT_BILLING.LISTING_CREDITS.PER_LISTING', {
                  price: formatPrice(
                    pkg.amount / pkg.listing_count,
                    pkg.currency
                  ),
                })
              }}
            </span>
          </div>

          <ButtonV4
            sm
            solid
            blue
            class="justify-center mt-auto"
            :is-loading="buyingId === pkg.id"
            @click="buy(pkg)"
          >
            {{ $t('WGPT_BILLING.LISTING_CREDITS.BUY') }}
          </ButtonV4>
        </div>
        <p v-if="!packages.length" class="text-sm text-n-slate-11">
          {{ $t('WGPT_BILLING.LISTING_CREDITS.NO_PACKAGES') }}
        </p>
      </div>
    </BillingCard>

    <BillingCard
      :title="$t('WGPT_BILLING.LISTING_CREDITS.HISTORY_TITLE')"
      :description="$t('WGPT_BILLING.LISTING_CREDITS.HISTORY_DESCRIPTION')"
    >
      <div class="px-5 divide-y divide-n-weak">
        <div
          v-for="item in history"
          :key="item.id"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="flex flex-col gap-0.5 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(`WGPT_BILLING.LISTING_CREDITS.REASON.${item.reason}`) }}
            </span>
            <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span class="whitespace-nowrap">
                {{ formatDate(item.created_at) }}
              </span>
              <template v-if="item.listing">
                <span>·</span>
                <router-link
                  :to="{
                    name: 'wgpt_car_listings_editor',
                    params: { listingId: item.listing.id },
                  }"
                  class="truncate text-n-blue-11 hover:underline"
                >
                  {{ listingLabel(item.listing) }}
                </router-link>
              </template>
              <template v-else-if="item.package_name">
                <span>·</span>
                <span class="truncate">{{ item.package_name }}</span>
              </template>
            </div>
          </div>
          <div class="flex flex-col items-end gap-0.5 shrink-0">
            <span
              class="text-sm font-medium tabular-nums"
              :class="item.delta > 0 ? 'text-n-teal-11' : 'text-n-slate-11'"
            >
              {{ item.delta > 0 ? '+' : '' }}{{ item.delta }}
            </span>
            <div class="flex items-center gap-1.5 text-xs">
              <span v-if="item.amount" class="text-n-slate-10 tabular-nums">
                {{ formatPrice(item.amount, item.currency) }}
              </span>
              <button
                v-if="item.has_invoice"
                class="text-n-blue-11 hover:underline disabled:opacity-50"
                :disabled="invoiceLoadingId === item.id"
                @click="openInvoice(item)"
              >
                {{ $t('WGPT_BILLING.LISTING_CREDITS.VIEW_INVOICE') }}
              </button>
            </div>
          </div>
        </div>
        <p v-if="!history.length" class="py-3 text-sm text-n-slate-11">
          {{ $t('WGPT_BILLING.LISTING_CREDITS.HISTORY_EMPTY') }}
        </p>
      </div>
    </BillingCard>
  </section>
</template>
